import type { Node, Edge } from '../../../core/models/scene';
import {
  ProcessLayoutService,
  type LayoutOptions,
} from './ProcessLayoutService';
import {
  allocateEdgeAnchors,
  type AnchorSide,
} from './edgeAnchorAllocation';
import { findPoolTable, getLaneIndexForNode } from './laneContainment';
import { chooseAnchorPairForStrategy, type Rect } from './flowchartAnchors';
import {
  ProcessEdgeRoutingService,
  type ProcessEdgeRouteUpdate,
} from './ProcessEdgeRoutingService';

export interface SwimlaneLayoutResult {
  /** Per-node move from old to new canvas position. Empty when nothing changed. */
  moves: Array<{
    nodeId: string;
    oldPosition: { x: number; y: number };
    newPosition: { x: number; y: number };
  }>;
  /** Pool dimension updates when laid-out content overflows the current rect. */
  poolUpdate: { id: string; updates: Partial<Node> } | null;
  /** Per-edge anchor reallocation (sequence_flow only, only edges that actually moved). */
  edgeAllocations: Map<
    string,
    { fromAnchor: AnchorSide; toAnchor: AnchorSide; fromOffset: number; toOffset: number }
  >;
  /** Obstacle-aware route updates for process flow edges after layout. */
  edgeRoutes: Map<string, ProcessEdgeRouteUpdate>;
}

interface CanvasBand {
  canvasStart: number;
  size: number;
}

const HEADER_FALLBACK = 40;
const FLOW_PADDING = 24;
const RANK_NODE_FOOTPRINT = 140; // ~120 node + 20 padding

/**
 * Layout a swimlane (pool-table) flowchart in canvas space.
 *
 * Direct canvas-space placement — no round-trip through pool-local
 * coordinates. Reads the pool's current rect, divides it into lane bands
 * (from `attrs.rowHeights` / `attrs.columnWidths` or equal thirds), runs
 * ELK on the inner shapes for flow-axis ordering with a topological-rank
 * fallback when the ELK worker URL fails to resolve, then places each
 * shape at its band's cross-axis centre. Pool grows along the flow axis
 * to fit overflowing content; never shrinks.
 *
 * Works regardless of how the pool was created — `findPoolTable` accepts
 * both the RDM-hydrated `__isPoolTable` flag and editor-spawned
 * `schemaId === 'swimlane-{horizontal,vertical}'`.
 */
export class ProcessSwimlaneLayoutService {
  private layoutService: ProcessLayoutService;
  private edgeRoutingService: ProcessEdgeRoutingService;

  constructor(layoutService?: ProcessLayoutService, edgeRoutingService?: ProcessEdgeRoutingService) {
    this.layoutService = layoutService ?? new ProcessLayoutService();
    this.edgeRoutingService = edgeRoutingService ?? new ProcessEdgeRoutingService();
  }

  static appliesTo(nodes: Node[]): Node | undefined {
    return findPoolTable(nodes);
  }

  async layout(
    nodes: Node[],
    edges: Edge[],
    options: LayoutOptions,
  ): Promise<SwimlaneLayoutResult> {
    const pool = findPoolTable(nodes);
    if (!pool) {
      return { moves: [], poolUpdate: null, edgeAllocations: new Map(), edgeRoutes: new Map() };
    }

    const poolAttrs = (pool.attrs ?? {}) as Record<string, unknown>;
    const poolX = pool.x ?? 0;
    const poolY = pool.y ?? 0;
    const poolW = pool.w ?? pool.width ?? 0;
    const poolH = pool.h ?? pool.height ?? 0;
    const direction = resolveDirection(poolAttrs);
    const flowHeader = resolveFlowHeader(poolAttrs, direction);
    const bands = resolveBands(poolAttrs, direction, poolX, poolY, poolW, poolH);

    const innerNodes = nodes.filter((n) => n.id !== pool.id);
    if (innerNodes.length === 0) {
      return { moves: [], poolUpdate: null, edgeAllocations: new Map(), edgeRoutes: new Map() };
    }

    const laneIndexById = computeLaneIndexes(innerNodes, nodes, bands.length);
    const rankById = computeTopologicalRanks(innerNodes, edges);

    // Try ELK first for flow-axis ordering. In dev the elkjs worker URL
    // sometimes returns the no-op fallback (graph unchanged, no x/y), so
    // we always compute a topological-rank fallback and use ELK's value
    // when available, else the rank.
    const layoutResult = await this.layoutService.applyLayout(innerNodes, edges, {
      strategy: direction === 'horizontal' ? 'left-to-right' : 'top-to-bottom',
      nodeSpacing: options.nodeSpacing ?? 80,
      rankSpacing: options.rankSpacing ?? 100,
    });
    const useElk = layoutResult.nodePositions.size > 0;
    const rankStride = (options.nodeSpacing ?? 80) + RANK_NODE_FOOTPRINT;

    const moves: SwimlaneLayoutResult['moves'] = [];
    let maxFlowEnd = 0;
    for (const node of innerNodes) {
      const laneIdx = laneIndexById.get(node.id) ?? 0;
      const band = bands[laneIdx]!;
      const rank = rankById.get(node.id) ?? 0;
      const w = node.w ?? node.width ?? 120;
      const h = node.h ?? node.height ?? 50;

      const elkPos = useElk ? layoutResult.nodePositions.get(node.id) : undefined;
      const flowAxisLocal = elkPos
        ? direction === 'horizontal'
          ? elkPos.x
          : elkPos.y
        : rank * rankStride;

      let newX: number;
      let newY: number;
      if (direction === 'horizontal') {
        newX = poolX + flowHeader + flowAxisLocal;
        newY = band.canvasStart + (band.size - h) / 2;
        maxFlowEnd = Math.max(maxFlowEnd, flowHeader + flowAxisLocal + w);
      } else {
        newX = band.canvasStart + (band.size - w) / 2;
        newY = poolY + flowHeader + flowAxisLocal;
        maxFlowEnd = Math.max(maxFlowEnd, flowHeader + flowAxisLocal + h);
      }

      const oldX = node.x ?? 0;
      const oldY = node.y ?? 0;
      if (oldX !== newX || oldY !== newY) {
        moves.push({
          nodeId: node.id,
          oldPosition: { x: oldX, y: oldY },
          newPosition: { x: newX, y: newY },
        });
      }
    }

    const poolUpdate = computePoolResize(pool, direction, poolW, poolH, maxFlowEnd);

    const edgeAllocations = computeEdgeAllocations(
      innerNodes,
      edges,
      moves,
      options.strategy,
    );

    const projectedNodes = projectNodesForRouting(nodes, moves, poolUpdate);
    const projectedEdges = projectEdgesForRouting(edges, edgeAllocations);
    const edgeRoutes = this.edgeRoutingService.route(projectedNodes, projectedEdges, {
      layoutSource: 'process-swimlane',
    });

    return { moves, poolUpdate, edgeAllocations, edgeRoutes };
  }
}

function projectNodesForRouting(
  nodes: Node[],
  moves: SwimlaneLayoutResult['moves'],
  poolUpdate: SwimlaneLayoutResult['poolUpdate'],
): Node[] {
  const moveById = new Map(moves.map((m) => [m.nodeId, m.newPosition] as const));
  return nodes.map((node) => {
    const move = moveById.get(node.id);
    const update = poolUpdate?.id === node.id ? poolUpdate.updates : undefined;
    return {
      ...node,
      ...(move ? { x: move.x, y: move.y } : {}),
      ...(update ?? {}),
      attrs: { ...(node.attrs ?? {}) },
    } as Node;
  });
}

function projectEdgesForRouting(
  edges: Edge[],
  edgeAllocations: SwimlaneLayoutResult['edgeAllocations'],
): Edge[] {
  return edges.map((edge) => {
    const allocation = edgeAllocations.get(edge.id);
    if (!allocation) return { ...edge, attrs: { ...(edge.attrs ?? {}) } } as Edge;
    return {
      ...edge,
      fromAnchor: edge.fromAnchorLocked === true
        ? edge.fromAnchor ?? allocation.fromAnchor
        : allocation.fromAnchor,
      toAnchor: edge.toAnchorLocked === true
        ? edge.toAnchor ?? allocation.toAnchor
        : allocation.toAnchor,
      fromOffset: edge.fromAnchorLocked === true
        ? edge.fromOffset ?? allocation.fromOffset
        : allocation.fromOffset,
      toOffset: edge.toAnchorLocked === true
        ? edge.toOffset ?? allocation.toOffset
        : allocation.toOffset,
      attrs: { ...(edge.attrs ?? {}) },
    } as Edge;
  });
}

function resolveDirection(poolAttrs: Record<string, unknown>): 'horizontal' | 'vertical' {
  const schemaId = typeof poolAttrs.schemaId === 'string' ? poolAttrs.schemaId : undefined;
  if (schemaId === 'swimlane-vertical') return 'vertical';
  if (schemaId === 'swimlane-horizontal') return 'horizontal';
  if (typeof poolAttrs.laneDirection === 'string') {
    return poolAttrs.laneDirection === 'vertical' ? 'vertical' : 'horizontal';
  }
  return 'horizontal';
}

function resolveFlowHeader(
  poolAttrs: Record<string, unknown>,
  direction: 'horizontal' | 'vertical',
): number {
  if (direction === 'horizontal') {
    const cw = poolAttrs.columnWidths;
    if (Array.isArray(cw) && cw.length > 0 && typeof cw[0] === 'number') return cw[0];
  } else {
    const rh = poolAttrs.rowHeights;
    if (Array.isArray(rh) && rh.length > 0 && typeof rh[0] === 'number') return rh[0];
  }
  return HEADER_FALLBACK;
}

function resolveBands(
  poolAttrs: Record<string, unknown>,
  direction: 'horizontal' | 'vertical',
  poolX: number,
  poolY: number,
  poolW: number,
  poolH: number,
): CanvasBand[] {
  const sizesAttr = direction === 'horizontal' ? poolAttrs.rowHeights : poolAttrs.columnWidths;
  if (Array.isArray(sizesAttr) && sizesAttr.length > 0) {
    const out: CanvasBand[] = [];
    let cursor = direction === 'horizontal' ? poolY : poolX;
    for (const s of sizesAttr) {
      if (typeof s !== 'number') continue;
      out.push({ canvasStart: cursor, size: s });
      cursor += s;
    }
    if (out.length > 0) return out;
  }
  const totalSize = direction === 'horizontal' ? poolH : poolW;
  const bandSize = totalSize / 3;
  const start = direction === 'horizontal' ? poolY : poolX;
  return [0, 1, 2].map((i) => ({ canvasStart: start + i * bandSize, size: bandSize }));
}

function computeLaneIndexes(
  innerNodes: Node[],
  allNodes: Node[],
  laneCount: number,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const node of innerNodes) {
    const idx = getLaneIndexForNode(node, allNodes) ?? 0;
    map.set(node.id, Math.max(0, Math.min(idx, laneCount - 1)));
  }
  return map;
}

/**
 * Longest-path topological rank. Roots (no incoming edges) get rank 0.
 * Within each rank, ELK or current geometry decides ordering. Bounded
 * iteration count guards against cycles.
 */
function computeTopologicalRanks(innerNodes: Node[], edges: Edge[]): Map<string, number> {
  const innerNodeIds = new Set(innerNodes.map((n) => n.id));
  const sequenceEdges = edges.filter((e) => {
    const t = e.type as string;
    return t === 'sequence_flow' || t === 'message_flow' || t === 'exception_flow' || t === 'data_association';
  });
  const incoming = new Map<string, string[]>();
  for (const e of sequenceEdges) {
    if (!innerNodeIds.has(e.from) || !innerNodeIds.has(e.to)) continue;
    const list = incoming.get(e.to) ?? [];
    list.push(e.from);
    incoming.set(e.to, list);
  }

  const rankById = new Map<string, number>();
  for (const node of innerNodes) {
    if (!incoming.has(node.id)) rankById.set(node.id, 0);
  }

  let changed = true;
  let iterations = 0;
  while (changed && iterations < innerNodes.length + 1) {
    changed = false;
    iterations += 1;
    for (const e of sequenceEdges) {
      if (!innerNodeIds.has(e.from) || !innerNodeIds.has(e.to)) continue;
      const fromRank = rankById.get(e.from) ?? 0;
      const required = fromRank + 1;
      const currentTo = rankById.get(e.to);
      if (currentTo === undefined || required > currentTo) {
        rankById.set(e.to, required);
        changed = true;
      }
    }
  }

  for (const node of innerNodes) {
    if (!rankById.has(node.id)) rankById.set(node.id, 0);
  }
  return rankById;
}

function computePoolResize(
  pool: Node,
  direction: 'horizontal' | 'vertical',
  currentW: number,
  currentH: number,
  maxFlowEnd: number,
): { id: string; updates: Partial<Node> } | null {
  const required = maxFlowEnd + FLOW_PADDING;
  const updates: Partial<Node> = {};
  if (direction === 'horizontal' && required > currentW) {
    updates.w = required;
  } else if (direction === 'vertical' && required > currentH) {
    updates.h = required;
  }
  return Object.keys(updates).length > 0 ? { id: pool.id, updates } : null;
}

function computeEdgeAllocations(
  innerNodes: Node[],
  edges: Edge[],
  moves: SwimlaneLayoutResult['moves'],
  strategy: LayoutOptions['strategy'],
): SwimlaneLayoutResult['edgeAllocations'] {
  const movedIds = new Set(moves.map((m) => m.nodeId));
  if (movedIds.size === 0) return new Map();

  const newRectById = new Map<string, Rect>();
  const moveById = new Map(moves.map((m) => [m.nodeId, m.newPosition] as const));
  for (const node of innerNodes) {
    const pos = moveById.get(node.id);
    const x = pos?.x ?? node.x ?? 0;
    const y = pos?.y ?? node.y ?? 0;
    const w = node.w ?? node.width ?? 120;
    const h = node.h ?? node.height ?? 50;
    newRectById.set(node.id, { x, y, w, h, cx: x + w / 2, cy: y + h / 2 });
  }

  const candidateEdges = edges
    .filter((e) => (e.type as string) === 'sequence_flow')
    .filter((e) => movedIds.has(e.from) || movedIds.has(e.to))
    .map((e) => ({
      id: e.id,
      from: e.from,
      to: e.to,
      fromAnchor: e.fromAnchor as AnchorSide | undefined,
      toAnchor: e.toAnchor as AnchorSide | undefined,
      fromOffset: e.fromOffset,
      toOffset: e.toOffset,
    }));

  return allocateEdgeAnchors(
    candidateEdges,
    (edge) => {
      const fromRect = newRectById.get(edge.from);
      const toRect = newRectById.get(edge.to);
      if (!fromRect || !toRect) return null;
      const pair = chooseAnchorPairForStrategy(fromRect, toRect, strategy);
      if (!pair.aSide || !pair.bSide) return null;
      return { fromSide: pair.aSide as AnchorSide, toSide: pair.bSide as AnchorSide };
    },
    { mode: 'redistribute' },
  );
}
